/** Display formatting only, per docs/design/04-frontend.md ("dates via Intl + small helpers"). */
export function formatDate(iso: string, locale = "en-GB"): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(value);
}

/** Shift an ISO date by whole years, keeping the day-of-month where it exists and
 * clamping to the month's last day where it doesn't (29 Feb in a non-leap year). Used
 * by the P/L report's year-step buttons: an accounting year shifted a whole year keeps
 * its alignment to the company's year end, which stepping by 365 days would not. */
export function shiftYears(iso: string, years: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const [, y, m, d] = match;
  const year = Number(y) + years;
  const month = Number(m);
  // Day 0 of the following month is the last day of this one.
  const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const day = Math.min(Number(d), lastDayOfMonth);
  return `${String(year).padStart(4, "0")}-${m}-${String(day).padStart(2, "0")}`;
}

export function formatDateTime(iso: string, locale = "en-GB"): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(
    value,
  );
}

/** Whole days from `iso` until `now`, floored; null if `iso` isn't a date. Used by the
 * stale-backup banner, where the server has already decided the age is over 48 h, so a
 * whole-day figure is always at least 2 and never has to say "0 days ago". */
export function daysSince(iso: string, now = new Date()): number | null {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return null;
  return Math.floor((now.getTime() - value.getTime()) / 86_400_000);
}
